/**
 * @fileoverview The video player module.
 * @author Nathan Buchar <nathan.buchar@odopod.com>
 * @since 1.0.0
 */

import $ from 'jquery';
import SparkTap from 'spark-tap';
import SparkVideo from 'spark-video';
import SparkViewportEnter from 'spark-viewport-enter';
import SparkWindowEvents from 'spark-window-events';
import { browser as Browser } from 'spark-helpers';
import Media from 'js/media';
import './video-player.scss';

const USER_AGENT = navigator.userAgent;

/**
 * @var {object} VideoPlayer
 * @description The Video Player module.
 * @param {element} element - The base element.
 * @constructor
 */
class VideoPlayer {
  constructor(element) {
    /**
     * @prop {element} _element
     * @description Base Element
     * @private
     */
    this._element = element;

    /**
     * @prop {jQuery} _$element
     * @description Base jQuery Element.
     * @private
     */
    this._$element = $(element);

    /**
     * @prop {SparkVideo} _sparkVideo
     * @description Spark Video instance for the Video Player.
     * @default null
     * @private
     */
    this._sparkVideo = null;

    /**
     * @prop {element} _sparkVideoElement
     * @description Spark Video element for the Spark Video.
     * @default null
     * @private
     */
    this._sparkVideoElement = null;

    /**
     * @prop {element} _sparkVideoControlsElement
     * @description The controls element for the Spark Video.
     * @default null
     * @private
     */
    this._sparkVideoControlsElement = null;

    /**
     * @prop {element} _videoOverlayElement
     * @description The video overlay element; Contains the poster and play button.
     * @default null
     * @private
     */
    this._videoOverlayElement = null;

    /**
     * @prop {number} _viewportEnterId
     * @description The unique id for this module's Spark Viewport Enter reference.
     * @default null
     * @private
     */
    this._viewportEnterId = null;

    /**
     * @prop {Boolean} _isPlaying
     * @description Indicates whether the video is currently playing or not.
     * @default false
     * @private
     */
    this._isPlaying = false;

    /**
     * @prop {Integer} _interactionState
     * @description The current interaction state of the player. The player
     *   changes how it acts based on the interaction--or lackthereof--of the user.
     * @default null
     * @private
     */
    this._interactionState = null;

    /**
     * @prop {Boolean} _isIOS
     * @description A boolean indicating whether the current system is iOS.
     * @private
     */
    this._isIOS = Browser.isIOS(USER_AGENT);

    /**
     * @prop {Boolean} _isAndroidOS
     * @description A boolean indicating whether the current system is Android.
     * @private
     */
    this._isAndroidOS = Browser.isAndroidOS(USER_AGENT);

    /**
     * @prop {jQuery.Deferred} _videoCanPlayThrough
     * @description Promise indicating whether the video can play through without
     *   interruption.
     * @private
     */
    this._videoCanPlayThrough = $.Deferred();

    this._init();
  }

  /**
   * Will initialize the main module. And delegate all tasks.
   *
   * @private
   */
  _init() {
    this._bindElements();
    this._initListeners();
    this._initViewportEnter();
    this._initSparkVideo();
    this._setStateInactive();
    this._updateControls();
  }

  /**
   * Binds all relevant elements to the Video Player instance.
   *
   * @private
   */
  _bindElements() {
    this._sparkVideoElement = this._$element.find(`.${SparkVideo.settings.Classes.BASE}`).get(0);
    this._videoOverlayElement = this._$element.find(VideoPlayer.Selectors.OVERLAY).get(0);
  }

  /**
   * Will initialize event listeners for the module.
   *
   * @private
   */
  _initListeners() {
    SparkTap.addListener(this._sparkVideoElement, this._handleUserInteraction.bind(this));
    SparkTap.addListener(this._videoOverlayElement, this._handlePosterElementClick.bind(this));

    SparkWindowEvents.onResize(this._handleResize.bind(this));
  }

  /**
   * First disposes of any pre-existing Spark Viewport Enter Video Player
   * references, then registers the Video Player with Spark Viewport Enter.
   *
   * @see _disposeViewportEnter, _spawnViewportEnter
   * @private
   */
  _initViewportEnter() {
    this._disposeViewportEnter();
    this._registerViewportEnter();
  }

  /**
   * Registers the Video Player with Spark Viewport Enter.
   *
   * @private
   */
  _registerViewportEnter() {
    this._viewportEnterId = SparkViewportEnter.add({
      element: this._element,
      enter: this._handleViewportEnter.bind(this),
      exit: this._handleViewportExit.bind(this),
    });
  }

  /**
   * Dispose of the Spark Viewport Enter Video Player reference if one exists.
   *
   * @private
   */
  _disposeViewportEnter() {
    if (this._viewportEnterId !== null) {
      SparkViewportEnter.remove(this._viewportEnterId);
    }
  }

  /**
   * Will initialize the Spark Video instance for this video player, as well as
   * mute the video initially, and listening and approrpriately delegating for
   * `canplaythrough` and `ended` events.
   *
   * @private
   */
  _initSparkVideo() {
    const opts = {};

    if (this._element.getElementsByClassName(VideoPlayer.ClassName.VIDEO_FRAME).length) {
      opts.controls = SparkVideo.settings.Controls.NONE;
    }

    this._sparkVideo = new SparkVideo(this._sparkVideoElement, opts);
    this._sparkVideoEl = this._sparkVideo.videoEl;

    // Bind the controls element; This must be done after SparkVideo has been
    // initialized, because the controls are inserted into the DOM dynamically.
    this._sparkVideoControlsElement = this._$element.find(`.${SparkVideo.settings.Classes.CONTROLS}`).get(0);

    // Immediately mute the video.
    if (VideoPlayer.Settings.MUTE_VIDEO_WHEN_AUTOPLAYING) {
      this._hardMute();
    }

    // Register video event listeners and bind their respective delegates.
    $(this._sparkVideoEl).on('canplaythrough', this._handleVideoCanPlayThrough.bind(this));
    $(this._sparkVideoEl).on('ended', this._handleVideoEnded.bind(this));
    $(this._sparkVideoEl).on('play', this._handleVideoPlay.bind(this));
    $(this._sparkVideoEl).on('pause', this._handleVideoPause.bind(this));
    $(this._sparkVideoEl).on('stalled', this._handleVideoStall.bind(this));
    $(this._sparkVideoEl).on('seeked', this._handleVideoSeek.bind(this));
  }

  /**
   * Called when the video player module enters the viewport. If the user has not
   * interacted with the video, then we will play the video as it enters the
   * viewport. However, if the user has interacted with the video in some way, we
   * will leave it up to the user to play the video.
   *
   * No longer necessary to wait until the video can play through entirely.
   * See ODO-322.
   * - @NB
   *
   * @private
   */
  _handleViewportEnter() {
    if (!Media.isMobile && !this._isIOS && !this._isAndroidOS) {
      if (VideoPlayer.Settings.AUTOPLAY_VIDEO_WHEN_INACTIVE &&
          this._interactionState === VideoPlayer.States.INACTIVE) {
        this._play();
      }
    }
  }

  /**
   * Called when the video player module exits the viewport. If the user has not
   * interacted with the video, then we will pause the video as it exits the
   * viewport. However, if the user has interacted with the video in some way, we
   * will leave it up to the user to pause the video.
   *
   * @private
   */
  _handleViewportExit() {
    if (!Media.isMobile && !this._isIOS && !this._isAndroidOS) {
      if (VideoPlayer.Settings.AUTOPLAY_VIDEO_WHEN_INACTIVE &&
          this._interactionState === VideoPlayer.States.INACTIVE) {
        this._pause();
      }
    }
  }

  /**
   * Called when the user intracts with the video in any way.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleUserInteraction() {
    if (this._interactionState !== VideoPlayer.States.ACTIVE) {
      this._setStateActive();
      this._unmute();
    }
  }

  /**
   * Called when the user clicks the poster when active. This will trigger an
   * interaction event and play the video.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handlePosterElementClick() {
    this._handleUserInteraction();
    this._play();
  }

  /**
   * Called when the video can play through fully without stopping.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoCanPlayThrough() {
    this._videoCanPlayThrough.resolve();
  }

  /**
   * Called when the video begins playing.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoPlay() {
    this._isPlaying = true;
    this._dispatch(VideoPlayer.EventType.PLAY, {
      currentTime: this._sparkVideo.getCurrentTime(),
    });
  }

  /**
   * Called when the video is paused.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoPause() {
    this._isPlaying = false;
    this._dispatch(VideoPlayer.EventType.PAUSE, {
      currentTime: this._sparkVideo.getCurrentTime(),
    });

    if (this._isIOS) {
      this._applyVideoOverlay();
    }
  }

  /**
   * Called when the video has ended.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoEnded() {
    this._dispatch(VideoPlayer.EventType.END);

    switch (this._interactionState) {
      case VideoPlayer.States.ACTIVE:
        this._applyVideoOverlay();

        if (VideoPlayer.Settings.LOSE_INTERACTIVE_STATE_AFTER_VIDEO_END) {
          this._setStateInactive();

          if (VideoPlayer.Settings.MUTE_VIDEO_WHEN_AUTOPLAYING) {
            this._hardMute();
          }
        }

        break;
      default:
        if (VideoPlayer.Settings.LOOP_VIDEO_WHEN_AUTOPLAYING) {
          this._play();
        }
    }
  }

  /**
   * Called when the video stalls.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoStall() {
    this._dispatch(VideoPlayer.EventType.STALL, {
      currentTime: this._sparkVideo.getCurrentTime(),
    });
  }

  /**
   * Called when the user seeks within the video.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleVideoSeek() {
    this._dispatch(VideoPlayer.EventType.SEEK, {
      currentTime: this._sparkVideo.getCurrentTime(),
    });
  }

  /**
   * Called when the window is resized.
   *
   * @param {Object} evt - The Event Object.
   * @private
   */
  _handleResize() {
    this._updateControls();
  }

  /**
   * Applies the video poster to the Video Player. This is typically done when the
   * video hasn't yet loaded or when the video has ended after The user has
   * interacted with it in some way (instead of looping).
   *
   * @private
   */
  _applyVideoOverlay() {
    $(this._videoOverlayElement).stop().fadeIn(250);
  }

  /**
   * Hides the video poster. This is typically done when the video can play
   * through without interruption and begins to play, or when the user has chosen
   * to play the video.
   *
   *  @private
   */
  _hideVideoOverlay() {
    $(this._videoOverlayElement).stop().fadeOut(250);
  }

  /**
   * Determins whether or not we need to toggle the video controls via a modifer
   * class.
   *
   * @private
   */
  _updateControls() {
    if (Media.isMobile) {
      this._hideControls();
    } else {
      this._showControls();
    }
  }

  /**
   * Shows the controls.
   *
   * @private
   */
  _showControls() {
    this._sparkVideoControlsElement.classList.add(VideoPlayer.ClassName.SHOW_CONTROLS);
  }

  /**
   * Hides the controls.
   *
   * @private
   */
  _hideControls() {
    this._sparkVideoControlsElement.classList.remove(VideoPlayer.ClassName.SHOW_CONTROLS);
  }

  /**
   * Play the video via SparkVideo.play.
   *
   * @see SparkVideo.play
   * @private
   */
  _play() {
    if (!this._isPlaying && this._sparkVideo !== null) {
      this._hideVideoOverlay();
      this._sparkVideo.play();
    }
  }

  /**
   * Pauses the video via SparkVideo.pause.
   *
   * @see SparkVideo.pause
   * @private
   */
  _pause() {
    if (this._isPlaying && this._sparkVideo !== null) {
      this._sparkVideo.pause();
    }
  }

  /**
   * Instantly mutes the video player without any fade.
   *
   * @private
   */
  _hardMute() {
    this._sparkVideo._hardMute((volume) => {
      this._dispatch(VideoPlayer.EventType.MUTE, {
        volume,
      });
    });
  }

  /**
   * Mute the video player.
   *
   * @private
   */
  _mute() {
    this._sparkVideo.mute((volume) => {
      this._dispatch(VideoPlayer.EventType.MUTE, {
        volume,
      });
    });
  }

  /**
   * Unmute the video player.
   *
   * @private
   */
  _unmute() {
    this._sparkVideo.unmute((volume) => {
      this._dispatch(VideoPlayer.EventType.UNMUTE, {
        volume,
      });
    });
  }

  /**
   * Set the player state to aactive.
   *
   * @see _setInteractionState
   * @private
   */
  _setStateActive() {
    this._setInteractionState(VideoPlayer.States.ACTIVE);
    this._sparkVideoElement.classList.remove(VideoPlayer.ClassName.INACTIVE);
  }

  /**
   * Set the player state to inactive.
   *
   * @see _setInteractionState
   * @private
   */
  _setStateInactive() {
    this._setInteractionState(VideoPlayer.States.INACTIVE);
    this._sparkVideoElement.classList.add(VideoPlayer.ClassName.INACTIVE);
  }

  /**
   * Sets the interaction state for the player if the new state is is not the same
   * as the previous state.
   *
   * @param {Integer} state - The state enum.
   * @private
   */
  _setInteractionState(state) {
    if (this._interactionState !== state) {
      this._interactionState = state;
      this._dispatch(VideoPlayer.EventType.STATE_CHANGED, {
        state,
      });
    }
  }

  /**
   * Fire an event, causing all handlers for that event name to run.
   *
   * @param {string} eventName - Name of the event.
   * @param {object} [eventData={}] - The data provided to each handler.
   * @private
   */
  _dispatch(eventName, eventData) {
    if (typeof eventName === 'string') {
      this._$element.trigger(eventName, eventData || {});
    }
  }

  /**
   * Mute the video player.
   *
   * @access public
   */
  mute() {
    this._sparkVideo.mute();
  }

  /**
   * Unmute the video player.
   *
   * @access public
   */
  unmute() {
    this._sparkVideo.unmute();
  }

  /**
   * Public play method for the Video Player.
   *
   * @alias _play
   * @access public
   */
  play() {
    this._play();
  }

  /**
   * Public pause method for the Video Player.
   *
   * @alias _pause
   * @access public
   */
  pause() {
    this._pause();
  }

  /**
   * Returns the Video Player's current interaction state.
   *
   * @returns {Integer}
   * @access public
   */
  getInteractionState() {
    return this._interactionState;
  }

  /**
   * Returns the Video Player module's base element.
   *
   * @returns {element}
   * @access public
   */
  getElement() {
    return this._element;
  }

  /**
   * Appropriately dispose of this VideoPlayer instance.
   *
   * @access public
   */
  dispose() {
    this._element = null;
    this._$element = null;
    this._sparkVideo = null;
    this._sparkVideoElement = null;
    this._sparkVideoControlsElement = null;
    this._videoOverlayElement = null;
  }

  /**
   * Initializes all the modules within the page.
   *
   * @returns {object}
   * @static
   */
  static initializeAll() {
    return $('.video-player').map((i, el) => new VideoPlayer(el)).get();
  }
}

/**
 * @enum {String} ClassName
 */
VideoPlayer.ClassName = {
  BASE: 'video-player',
  SHOW_CONTROLS: 'spark-video__controls--show',
  INACTIVE: 'spark-video--inactive',
  VIDEO_FRAME: 'video-frame',
};

/**
 * @enum {String} Selectors
 */
VideoPlayer.Selectors = {
  BASE: '.video-player',
  OVERLAY: '.video-player__overlay',
};

/**
 * @enum {String} EventType
 */
VideoPlayer.EventType = {
  PLAY: 'videoPlayerDidPlay',
  PAUSE: 'videoPlayerDidPause',
  END: 'videoPlayerDidEnd',
  STALL: 'videoPlayerDidStall',
  SEEK: 'videoPlayerDidSeek',
  MUTE: 'videoPlayerDidMute',
  UNMUTE: 'videoPlayerDidUnmute',
  STATE_CHANGED: 'videoPlayerInteractionStateDidChange',
};

/**
 * @enum {String} States - Interaction states for the Video Player.
 */
VideoPlayer.States = {
  INACTIVE: 0,
  ACTIVE: 1,
};

/**
 * @enum {String} Volume - Volume settings.
 */
VideoPlayer.Volume = {
  MAX: 0.5,
  MUTE: 0,
  DURATION: 350,
};

/**
 * @enum {String} Settings - Global immutable Video Player settings.
 */
VideoPlayer.Settings = {
  AUTOPLAY_VIDEO_WHEN_INACTIVE: true,
  LOOP_VIDEO_WHEN_AUTOPLAYING: true,
  MUTE_VIDEO_WHEN_AUTOPLAYING: true,
  LOSE_INTERACTIVE_STATE_AFTER_VIDEO_END: true,
};

/**
 * New method for SparkVideo that will transition between the current volume
 * level and a new volume level with a custom duration and callback.
 *
 * @param {Number} newVolumeLevel - The new volume level as a number between 0
 *   and 1 inclusively.
 * @param {Number} [duration=350ms] - The volume transition duration.
 * @param {Function} [callback]
 * @private
 */
SparkVideo.prototype._transitionVolumeLevel = function _transitionVolumeLevel(
  newVolumeLevel, duration = VideoPlayer.Volume.DURATION, callback) {
  if (typeof newVolumeLevel !== 'undefined') {
    $(this.videoEl).stop().animate({
      volume: newVolumeLevel,
    }, duration, () => {
      callback(this.getVolumeLevel());
    });
  }
};

/**
 * Rewrite the default SparkVideo "mute" method has "hardMute". This method will
 * immediately mute the video without any fade.
 *
 * @param {Function} [callback]
 * @private
 */
SparkVideo.prototype._hardMute = function _hardMute(callback = $.noop) {
  this.videoEl.volume = 0;
  this.element.classList.add(SparkVideo.settings.Classes.IS_MUTED);

  callback(0);
};

/**
 * Override SparkVideo's `mute` method to allow for more elegant volume
 * transitions.
 *
 * @param {Function} [callback]
 * @override
 */
SparkVideo.prototype.mute = function mute(callback = $.noop) {
  this.element.classList.add(SparkVideo.settings.Classes.IS_MUTED);

  this._transitionVolumeLevel(
    VideoPlayer.Volume.MUTE,
    VideoPlayer.Volume.DURATION,
    callback,
  );
};

/**
 * Override SparkVideo's `unmute` method to allow for more elegant volume
 * transitions.
 *
 * @param {Function} [callback]
 * @override
 */
SparkVideo.prototype.unmute = function unmute(callback = $.noop) {
  this.element.classList.remove(SparkVideo.settings.Classes.IS_MUTED);

  this._transitionVolumeLevel(
    VideoPlayer.Volume.MAX,
    VideoPlayer.Volume.DURATION,
    callback,
  );
};

/**
 * New method for SparkVideo that returns the current volume level.
 *
 * @returns {Number}
 * @access public
 */
SparkVideo.prototype.getVolumeLevel = function getVolumeLevel() {
  return this.videoEl.volume;
};

export default VideoPlayer;
